import { ContentType } from '@/@types/common'
import type { Post } from '@/@types/post'
import Avatar from '@/components/Avatar'
import { REPOST } from '@/constants/api'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { post } from '@/utils/request'
import { type HTMLAttributes, useCallback, useEffect, useMemo, useRef } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import type { LexicalEditorHandle } from '../../Editor/LexicalEditor'
import AutoSaveService from '../AutoSaveService'
import ContentEditor from '../ContentEditor'
import PostCard from '../PostTargetCard/PostCard'
import RecoverDraftButton from '../RecoverDraftButton'
import styles from './RepostForm.module.css'
import SubmitButton from './SubmitButton'
import type { RepostFormValue } from './types'

export interface RepostFormProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
	target: Post
	onSubmit: (data: Post) => void
}

export default function RepostForm({ target, onSubmit, ...props }: RepostFormProps) {
	const contentEditorRef = useRef<{ editor: LexicalEditorHandle }>(null)
	const user = useCurrentUser()

	const defaultValues = useMemo(
		() => ({
			content: '',
			syncComment: false,
			targetId: target.id,
			targetType: target.type,
		}),
		[target]
	)
	const methods = useForm<RepostFormValue>({
		defaultValues,
	})
	const { isSubmitting } = methods.formState

	const handleSubmit = useCallback(
		(formValue: RepostFormValue) => {
			// @todo validate form value
			return post(REPOST, formValue).then((res) => {
				methods.reset()
				contentEditorRef.current?.editor.clear()
				onSubmit?.(res.data.data as Post)
			})
		},
		[methods, onSubmit]
	)

	useEffect(() => {
		contentEditorRef.current?.editor.editor?.focus()
	}, [])

	return (
		<div>
			<FormProvider {...methods}>
				<div className={styles.formContainer}>
					<Avatar data={user} className={styles.avatar} />
					<form onSubmit={methods.handleSubmit(handleSubmit)} className={styles.form}>
						<Controller
							name="content"
							render={({ field }) => (
								<ContentEditor
									autoFocus
									{...field}
									ref={contentEditorRef}
									onSubmit={methods.handleSubmit(handleSubmit)}
								/>
							)}
						/>

						<PostCard data={target} className={styles.postCard} />

						<footer className={styles.footer}>
							<RecoverDraftButton
								draftType={ContentType.REPOST}
								onRecover={(nextData) => {
									const draft = nextData as RepostFormValue
									for (const key in draft) {
										methods.setValue(
											key as keyof RepostFormValue,
											draft[key as keyof RepostFormValue],
											{
												shouldDirty: false,
											}
										)
									}
									contentEditorRef.current?.editor.setText(nextData.content)
								}}
							/>

							<SubmitButton isLoading={isSubmitting} className={styles.submitButton} />
						</footer>
						<AutoSaveService type={ContentType.REPOST} />
					</form>
				</div>
			</FormProvider>
		</div>
	)
}
